import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format, startOfMonth, startOfWeek, endOfWeek, endOfMonth } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { TrackerRecapGraphic } from './TrackerRecapGraphic';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iso = (d) => format(d, 'yyyy-MM-dd');

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'custom', label: 'Custom' },
];

const presetRange = (key) => {
  const now = new Date();
  if (key === 'today') return [iso(now), iso(now)];
  if (key === 'week') return [iso(startOfWeek(now, { weekStartsOn: 1 })), iso(endOfWeek(now, { weekStartsOn: 1 }))];
  return [iso(startOfMonth(now)), iso(endOfMonth(now))];
};

export const TrackerRecapModal = ({ open, onOpenChange }) => {
  const { user, getAuthHeader } = useAuth();
  const [preset, setPreset] = useState('today');
  const [custom, setCustom] = useState(() => presetRange('week'));
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(false);

  const [start, end] = useMemo(() => (preset === 'custom' ? custom : presetRange(preset)), [preset, custom]);
  const rangeLabel = PRESETS.find((p) => p.key === preset)?.label === 'Custom' ? 'Custom range' : PRESETS.find((p) => p.key === preset)?.label;

  useEffect(() => {
    if (!open || !start || !end) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/tracker/recap`, { ...getAuthHeader(), params: { start_date: start, end_date: end } })
      .then((res) => { if (!cancelled) setRecap(res.data); })
      .catch(() => { if (!cancelled) toast.error('Failed to load your activity'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, start, end, getAuthHeader]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[520px]" data-testid="tracker-recap-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Manrope'] text-xl font-extrabold text-[#2B3674]">
            <Share2 className="h-5 w-5 text-[#23ACD5]" /> Share your activity
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-1 rounded-full bg-[#F4F7FE] p-1" data-testid="tracker-recap-presets">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPreset(p.key)}
              className={`flex-1 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                preset === p.key ? 'bg-white text-[#2447D6] shadow-sm' : 'text-[#A3AED0] hover:text-[#2B3674]'
              }`}
              data-testid={`tracker-recap-preset-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {preset === 'custom' && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="recap-start">From</Label>
              <Input id="recap-start" type="date" value={custom[0]} max={custom[1]} onChange={(e) => setCustom([e.target.value, custom[1]])} data-testid="tracker-recap-start" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="recap-end">To</Label>
              <Input id="recap-end" type="date" value={custom[1]} min={custom[0]} onChange={(e) => setCustom([custom[0], e.target.value])} data-testid="tracker-recap-end" />
            </div>
          </div>
        )}

        {loading || !recap ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#2447D6]" />
          </div>
        ) : (
          <TrackerRecapGraphic recap={recap} rangeLabel={rangeLabel} agentName={user?.name} agentPicture={user?.profile_picture} />
        )}
      </DialogContent>
    </Dialog>
  );
};
